import { createContext, useContext, ReactNode } from 'react';
import { User } from '../types/schema';
import { useAuthContext } from './auth-context';
import { useWorkspace } from './workspace-context';
import { useWorkspaceUsers } from '../hooks/use-users-query';

interface UsersContextType {
  users: User[];
  loading: boolean;
  error: string | null;
  refetchUsers: () => Promise<void>;
}

const UsersContext = createContext<UsersContextType | null>(null);

/**
 * Users Provider
 * Provides a shared cache of workspace users to avoid duplicate API calls
 * Uses React Query with aggressive caching - users rarely change
 */
export function UsersProvider({ children }: { children: ReactNode }) {
  const { loading: authLoading } = useAuthContext();
  const { currentWorkspace } = useWorkspace();

  // Use React Query for user fetching with aggressive caching
  const { data: users = [], isLoading, error: queryError, refetch } = useWorkspaceUsers(currentWorkspace?.id);

  const loading = authLoading || isLoading;
  const error = queryError ? (queryError.message || null) : null;

  return (
    <UsersContext.Provider
      value={{
        users,
        loading,
        error,
        refetchUsers: async () => { await refetch(); },
      }}
    >
      {children}
    </UsersContext.Provider>
  );
}

export function useUsers() {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error('useUsers must be used within UsersProvider');
  }
  return context;
}
